'use client'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import type { PostHogInsightResult } from '@/lib/postHog'
import qs from 'qs'

import { useMemo, type ReactNode } from 'react'
import useSWR from 'swr'
import { usePostHogParam } from '../hooks/usePostHogContext'
import { useProjectStore } from '@/hooks/use-project-store'
import { fetcher, removePrefix } from '@/lib/utils'
import { countryNameToEmoji } from '../utils'
import { dayjs } from '@/utils/dayjs'
import { useTranslations } from 'next-intl'

export interface BreakdownItemProps {
  label?: string
  breakdown: string
  description?: string
  limit?: number
  onClick?: () => void
}

export function BreakdownItem(props: BreakdownItemProps) {
  const { label, description, breakdown, limit = 5, ...rest } = props
  const { project } = useProjectStore()
  const { date_from, event_name } = usePostHogParam()
  const t = useTranslations('analytics')
  const url = useMemo(() => {
    const queryObj = {
      date_from: dayjs.utc().subtract(date_from, 'day').toISOString(),
      breakdown,
      aggregation: ['dau'],
      ...(event_name && { event_name }),
    }

    const queryString = qs.stringify(queryObj, { arrayFormat: 'repeat' })

    return `/api/stats/projects/${project?._id.toString()}/insights?${queryString}`
  }, [project, date_from, breakdown, event_name])

  const { data, isLoading } = useSWR<PostHogInsightResult>(url, fetcher)
  const rows = useMemo(() => {
    const { result } = data?.data?.[0] || {}
    if (!Array.isArray(result) || result.length === 0) return []

    const validResult = result.filter(
      (d) => d.label !== '$$_posthog_breakdown_null_$$',
    )

    const max = Math.max(...validResult.map((d) => d.count))
    return validResult.slice(0, limit).map((d) => {
      let nextLabel = removePrefix(d.label, '$')
      if (breakdown === '$geoip_country_name') {
        const emoji = countryNameToEmoji(d.label)
        if (emoji) {
          nextLabel = `${emoji} ${nextLabel}`
        }
      }
      return {
        label: nextLabel,
        count: d.count,
        width: d.count === max ? 100 : Math.round((d.count / max) * 100),
      }
    })
  }, [data, breakdown, limit])

  return (
    <Card className="cursor-pointer bg-white/10" {...rest}>
      <CardHeader className="pb-2">
        <CardTitle>
          <div className="flex flex-between text-lg items-center">
            <div className="flex-1 font-bold">{label}</div>
            <div className="text-sm">{t('visitors')}</div>
          </div>
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="flex flex-col items-center gap-4">
              {/* Animated graph icon */}
              <div className="relative w-16 h-16">
                <svg
                  className="w-full h-full text-gold/70"
                  viewBox="0 0 100 100"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Graph line with animation */}
                  <path
                    d="M10 80 L25 60 L40 70 L55 40 L70 50 L85 30 L90 20"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                    strokeDasharray="200"
                    style={{
                      strokeDashoffset: 200,
                      animation: 'drawLine 2s ease-in-out infinite',
                    }}
                  />
                  {/* Data points with staggered pulse */}
                  <circle
                    cx="10"
                    cy="80"
                    r="4"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                  />
                  <circle
                    cx="25"
                    cy="60"
                    r="4"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                    style={{ animationDelay: '0.2s' }}
                  />
                  <circle
                    cx="40"
                    cy="70"
                    r="4"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                    style={{ animationDelay: '0.4s' }}
                  />
                  <circle
                    cx="55"
                    cy="40"
                    r="4"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                    style={{ animationDelay: '0.6s' }}
                  />
                  <circle
                    cx="70"
                    cy="50"
                    r="4"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                    style={{ animationDelay: '0.8s' }}
                  />
                  <circle
                    cx="85"
                    cy="30"
                    r="4"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                    style={{ animationDelay: '1s' }}
                  />
                  <circle
                    cx="90"
                    cy="20"
                    r="4"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                    style={{ animationDelay: '1.2s' }}
                  />
                </svg>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {rows.map((row) => (
              <div className="flex flex-between leading-[36px]" key={row.label}>
                <div className="flex-1 relative min-w-0">
                  <div
                    className="absolute inset-0 bg-[hsl(var(--chart-1))] opacity-10 transtion-all rounded-md"
                    style={{ width: `${row.width}%` }}
                  >
                    &nbsp;&nbsp;
                  </div>
                  <div className="relative z-10 truncate pl-2 text-sm leading-[36px]">
                    {row.label}
                  </div>
                </div>
                <div className="ml-2 w-[2em] pr-2 text-right">{row.count}</div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
